/**
 * Canimatedswitch Component
 * Generic animated content switcher with slide and fade transitions
 * Used for animating between different content states (tabs, forms, prices, etc.)
 *
 * ============================================================================
 * ANIMATION DIRECTION LOGIC - READ THIS BEFORE MODIFYING!
 * ============================================================================
 *
 * The `direction` value controls which way content slides:
 *
 *   direction = 1  → FORWARD animation (like clicking "Next" or "Proceed")
 *                    - New content enters from the RIGHT (starts at +translateAmount)
 *                    - Old content exits to the LEFT (ends at -translateAmount)
 *                    - Visual: content flows LEFT ← ← ←
 *
 *   direction = -1 → BACKWARD animation (like clicking "Back" or "Previous")
 *                    - New content enters from the LEFT (starts at -translateAmount)
 *                    - Old content exits to the RIGHT (ends at +translateAmount)
 *                    - Visual: content flows RIGHT → → →
 *
 * When using NUMERIC contentKey (recommended for sequential content):
 *   - Key increases (0→1→2→3) = FORWARD  = direction 1
 *   - Key decreases (3→2→1→0) = BACKWARD = direction -1
 *
 * The variants work as follows:
 *   initial.x = direction * translateAmount   (where new content STARTS)
 *   exit.x    = -direction * translateAmount  (where old content ENDS)
 *
 * Example with translateAmount=30:
 *   Forward (dir=1):  initial.x=+30 (from right), exit.x=-30 (to left)
 *   Backward (dir=-1): initial.x=-30 (from left), exit.x=+30 (to right)
 *
 * ============================================================================
 */
'use client';
import { type ReactNode, type CSSProperties, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { dataEl, type DataElScope } from '@/utils/dataEl';
export enum AnimationDirection {
    Left = 'left',
    Right = 'right'
}
const SWITCH_VARIANT_INITIAL = 'initial';
const SWITCH_VARIANT_ANIMATE = 'animate';
const SWITCH_VARIANT_EXIT = 'exit';
const ANIMATED_SWITCH_FORWARD_DIRECTION = 1;
const ANIMATED_SWITCH_BACKWARD_DIRECTION = -1;
export interface CanimatedswitchProps {
    dataElScope: DataElScope;
    /**
     * Content to display and animate
     */
    children: ReactNode;
    /**
     * Unique key that identifies the current content.
     * When this changes, the animation is triggered.
     *
     * For NUMERIC keys: direction is auto-calculated based on value comparison.
     * - Key increases → forward animation (enter from right)
     * - Key decreases → backward animation (enter from left)
     *
     * For STRING keys: uses the `direction` prop.
     */
    contentKey: string | number;
    /**
     * Animation direction (only used when contentKey is a string).
     * For numeric keys, direction is automatically determined by comparing values.
     *
     * - AnimationDirection.Right = forward animation (enter from right, exit to left)
     * - AnimationDirection.Left = backward animation (enter from left, exit to right)
     *
     * @default AnimationDirection.Right
     */
    direction?: AnimationDirection;
    /**
     * Distance in pixels for the slide animation
     * @default 30
     */
    translateAmount?: number;
    /**
     * Duration of the animation in milliseconds
     * @default 200
     */
    duration?: number;
    /**
     * Whether the wrapper should have flex: 1 1 0%
     * @default true
     */
    flex1?: boolean;
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional styles
     */
    style?: CSSProperties;
    /**
     * Style to apply to the content wrapper (the animated motion.div)
     */
    contentWrapperStyle?: CSSProperties;
}
interface SwitchDirectionState {
    contentKey: string | number;
    explicitDirection: AnimationDirection;
    directionValue: number;
}
function getExplicitDirectionValue(direction: AnimationDirection): number {
    return direction === AnimationDirection.Right
        ? ANIMATED_SWITCH_FORWARD_DIRECTION
        : ANIMATED_SWITCH_BACKWARD_DIRECTION;
}
function getNumericDirectionValue({ currentKey, previousKey, }: {
    currentKey: number;
    previousKey: number;
}): number {
    return currentKey > previousKey
        ? ANIMATED_SWITCH_FORWARD_DIRECTION
        : ANIMATED_SWITCH_BACKWARD_DIRECTION;
}
export function Canimatedswitch({ dataElScope, children, contentKey, direction = AnimationDirection.Right, translateAmount = 30, duration = 200, flex1 = true, className, style, contentWrapperStyle, }: CanimatedswitchProps) {
    /**
     * Calculate direction value based on key comparison:
     *
     *   direction = 1  → FORWARD (key increased, enter from right, exit to left)
     *   direction = -1 → BACKWARD (key decreased, enter from left, exit to right)
     *
     * For non-numeric keys, use the explicit `direction` prop.
     */
    const [switchDirectionState, setSwitchDirectionState] = useState<SwitchDirectionState>({
        contentKey,
        explicitDirection: direction,
        directionValue: getExplicitDirectionValue(direction),
    });
    let directionValue = switchDirectionState.directionValue;
    const hasContentKeyChanged = contentKey !== switchDirectionState.contentKey;
    const hasExplicitDirectionChanged = direction !== switchDirectionState.explicitDirection;
    if (hasContentKeyChanged || hasExplicitDirectionChanged) {
        if (hasContentKeyChanged &&
            typeof contentKey === 'number' &&
            typeof switchDirectionState.contentKey === 'number') {
            directionValue = getNumericDirectionValue({
                currentKey: contentKey,
                previousKey: switchDirectionState.contentKey,
            });
        }
        else {
            directionValue = getExplicitDirectionValue(direction);
        }
        setSwitchDirectionState({
            contentKey,
            explicitDirection: direction,
            directionValue,
        });
    }
    /**
     * Animation variants:
     *
     * The `dir` parameter is the directionValue (1 or -1).
     *
     * initial.x = dir * translateAmount
     *   - Forward (dir=1):  +30 → content starts 30px to the RIGHT
     *   - Backward (dir=-1): -30 → content starts 30px to the LEFT
     *
     * exit.x = -dir * translateAmount
     *   - Forward (dir=1):  -30 → content exits 30px to the LEFT
     *   - Backward (dir=-1): +30 → content exits 30px to the RIGHT
     */
    const variants: Variants = useMemo(() => ({
        initial: (dir: number) => ({
            opacity: 0,
            x: dir * translateAmount,
        }),
        animate: {
            opacity: 1,
            x: 0,
        },
        exit: (dir: number) => ({
            opacity: 0,
            x: -dir * translateAmount,
        }),
    }), [translateAmount]);
    return (
    <div data-el={dataEl(dataElScope, 'Root')} className={className} style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'clip',
            overflowClipMargin: '6px',
            position: 'relative',
            ...(flex1 && { flex: '1 1 0%' }),
            minHeight: 0,
            ...style,
        }}>
      <AnimatePresence mode="wait" initial={false} custom={directionValue}>
        <motion.div data-el={dataEl(dataElScope, 'Content')} key={contentKey} custom={directionValue} variants={variants} initial={SWITCH_VARIANT_INITIAL} animate={SWITCH_VARIANT_ANIMATE} exit={SWITCH_VARIANT_EXIT} transition={{ duration: duration / 1000, ease: 'linear' }} style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            ...contentWrapperStyle,
        }}>
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
    );
}
